/**
 * コーディングテストの練習の問題の本文 (問題の節と、入力と出力の例)。
 * 1 問の頁 ([slug].page.tsx) と模擬試験の頁 (mock.page.tsx) が同じ見た目で使う。
 */
import { MarkdownContent } from "@noiman/ui/markdown-content";
import { keepNumbersInProse, phraseText } from "@/lib/coding-interview";
import type { CodingTestExample } from "@/lib/coding-test";
import type { MdSection } from "@/lib/md-entry";
import { phrases } from "@/lib/phrase/phrase";

export const BODY_CLASS =
  "lab-article-prose md-fs-floor min-w-0 [&_:is(p,li,td,th)]:text-[17px] [&_pre]:![tab-size:2] [&_pre_code]:![tab-size:2] [&_pre]:!whitespace-pre-wrap [&_pre_code]:!whitespace-pre-wrap [&_:not(pre)>code]:whitespace-nowrap";
const WRAP = "[word-break:keep-all] [overflow-wrap:break-word]";

export function ProblemSection({ section }: { section: MdSection }) {
  return (
    <section id={section.id} className="scroll-mt-32">
      <MarkdownContent content={keepNumbersInProse(`## ${section.heading}\n\n${section.body}`)} className={BODY_CLASS} phraseSegment={phrases} />
      {section.items.map((item) => (
        <div key={item.id} id={item.id} className="scroll-mt-32">
          <MarkdownContent content={keepNumbersInProse(`### ${item.heading}\n\n${item.body}`)} className={BODY_CLASS} phraseSegment={phrases} />
        </div>
      ))}
    </section>
  );
}

const PRE = "m-0 overflow-x-auto whitespace-pre-wrap break-all rounded-lg border border-main bg-surface p-4 font-mono text-[16px] leading-[1.7] text-main";

/* 例は入力と出力に札を付けて並べる (原稿の囲みは入力と出力の text 2 つ) */
export function ExamplesBlock({ examples }: { examples: CodingTestExample[] }) {
  return (
    <section id="examples" className="scroll-mt-32">
      <h2 className="mt-8 text-[22px] font-bold text-main">入力と出力の例</h2>
      {examples.map((example) => (
        <div key={example.label} className="mt-4" data-stdio-example>
          <h3 className="m-0 text-[19px] font-bold text-main">{example.label}</h3>
          {example.note ? <p className={`mt-2 text-[17px] leading-[1.8] text-main ${WRAP}`}>{phraseText(keepNumbersInProse(example.note))}</p> : null}
          <p className="mb-1 mt-3 text-[17px] font-bold text-sub">入力</p>
          <pre className={PRE}>{example.input}</pre>
          <p className="mb-1 mt-3 text-[17px] font-bold text-sub">出力</p>
          <pre className={PRE}>{example.output}</pre>
        </div>
      ))}
    </section>
  );
}
